import { useState } from 'react';
import Icon from '../../../components/AppIcon';


const ShipmentDataGrid = ({ shipments, onRowSelect, onRowExpand, selectedRows, expandedRows }) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [editingCell, setEditingCell] = useState(null);

  const columns = [
    { key: 'select', label: '', width: '50px', sortable: false },
    { key: 'vesselName', label: 'Vessel Name', width: '180px', sortable: true, editable: false },
    { key: 'supplier', label: 'Supplier', width: '160px', sortable: true, editable: false },
    { key: 'buyer', label: 'Buyer', width: '160px', sortable: true, editable: false },
    { key: 'tonnage', label: 'Tonnage (MT)', width: '120px', sortable: true, editable: true },
    { key: 'coalGrade', label: 'Coal Grade', width: '120px', sortable: true, editable: false },
    { key: 'laycanFrom', label: 'Laycan From', width: '130px', sortable: true, editable: true },
    { key: 'laycanTo', label: 'Laycan To', width: '130px', sortable: true, editable: true },
    { key: 'etd', label: 'ETD', width: '120px', sortable: true, editable: true },
    { key: 'eta', label: 'ETA', width: '120px', sortable: true, editable: true },
    { key: 'pricingWindow', label: 'Pricing Window', width: '140px', sortable: true, editable: false },
    { key: 'lcStatus', label: 'LC Status', width: '130px', sortable: true, editable: false },
    { key: 'status', label: 'Status', width: '140px', sortable: true, editable: false },
    { key: 'actions', label: 'Actions', width: '100px', sortable: false }
  ];

  const handleSort = (key) => {
    if (!columns?.find(col => col?.key === key)?.sortable) return;
    
    let direction = 'asc';
    if (sortConfig?.key === key && sortConfig?.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const handleCellEdit = (shipmentId, field, value) => {
    console.log('Editing cell:', shipmentId, field, value);
    setEditingCell(null);
  };

  const getStatusColor = (status) => {
    const statusColors = {
      'contract-signed': 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
      'lc-received': 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
      'loading': 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
      'in-transit': 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400',
      'discharged': 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      'completed': 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
    };
    return statusColors?.[status] || 'bg-muted text-muted-foreground';
  };

  const getLCStatusColor = (status) => {
    const colors = {
      'received': 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      'pending': 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
      'not-required': 'bg-muted text-muted-foreground'
    };
    return colors?.[status] || 'bg-muted text-muted-foreground';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const isRowSelected = (id) => selectedRows?.includes(id);
  const isRowExpanded = (id) => expandedRows?.includes(id);

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      <div className="overflow-x-auto custom-scrollbar">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border sticky top-0 z-10">
            <tr>
              {columns?.map((column) => (
                <th
                  key={column?.key}
                  className="px-4 py-3 text-left text-xs font-semibold text-foreground uppercase tracking-wider"
                  style={{ width: column?.width, minWidth: column?.width }}
                >
                  {column?.key === 'select' ? (
                    <input
                      type="checkbox"
                      className="w-4 h-4 rounded border-border"
                      onChange={(e) => {
                        if (e?.target?.checked) {
                          onRowSelect(shipments?.map(s => s?.id));
                        } else {
                          onRowSelect([]);
                        }
                      }}
                      checked={selectedRows?.length === shipments?.length && shipments?.length > 0}
                    />
                  ) : (
                    <div
                      className={`flex items-center gap-2 ${column?.sortable ? 'cursor-pointer hover:text-primary' : ''}`}
                      onClick={() => column?.sortable && handleSort(column?.key)}
                    >
                      <span>{column?.label}</span>
                      {column?.sortable && sortConfig?.key === column?.key && (
                        <Icon
                          name={sortConfig?.direction === 'asc' ? 'ChevronUp' : 'ChevronDown'}
                          size={14}
                        />
                      )}
                    </div>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {shipments?.map((shipment) => (
              <>
                <tr
                  key={shipment?.id}
                  className={`hover:bg-muted/30 transition-colors ${
                    isRowSelected(shipment?.id) ? 'bg-primary/5' : ''
                  }`}
                >
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      className="w-4 h-4 rounded border-border"
                      checked={isRowSelected(shipment?.id)}
                      onChange={() => {
                        if (isRowSelected(shipment?.id)) {
                          onRowSelect(selectedRows?.filter(id => id !== shipment?.id));
                        } else {
                          onRowSelect([...selectedRows, shipment?.id]);
                        }
                      }}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => onRowExpand(shipment?.id)}
                        className="p-1 hover:bg-accent/10 rounded transition-colors"
                      >
                        <Icon
                          name={isRowExpanded(shipment?.id) ? 'ChevronDown' : 'ChevronRight'}
                          size={16}
                        />
                      </button>
                      <span className="font-medium text-foreground">{shipment?.vesselName}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-foreground">{shipment?.supplier}</td>
                  <td className="px-4 py-3 text-sm text-foreground">{shipment?.buyer}</td>
                  <td className="px-4 py-3 text-sm text-foreground font-data">
                    {shipment?.tonnage?.toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-sm text-foreground">{shipment?.coalGrade}</td>
                  <td className="px-4 py-3 text-sm text-foreground">{formatDate(shipment?.laycanFrom)}</td>
                  <td className="px-4 py-3 text-sm text-foreground">{formatDate(shipment?.laycanTo)}</td>
                  <td className="px-4 py-3 text-sm text-foreground">{formatDate(shipment?.etd)}</td>
                  <td className="px-4 py-3 text-sm text-foreground">{formatDate(shipment?.eta)}</td>
                  <td className="px-4 py-3 text-sm text-foreground">{shipment?.pricingWindow}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getLCStatusColor(shipment?.lcStatus)}`}>
                      {shipment?.lcStatus?.replace('-', ' ')?.toUpperCase()}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(shipment?.status)}`}>
                      {shipment?.status?.replace('-', ' ')?.toUpperCase()}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <button
                        className="p-1.5 hover:bg-accent/10 rounded transition-colors"
                        title="Edit shipment"
                      >
                        <Icon name="Edit2" size={16} color="var(--color-muted-foreground)" />
                      </button>
                      <button
                        className="p-1.5 hover:bg-accent/10 rounded transition-colors"
                        title="View details"
                      >
                        <Icon name="Eye" size={16} color="var(--color-muted-foreground)" />
                      </button>
                    </div>
                  </td>
                </tr>
                {isRowExpanded(shipment?.id) && (
                  <tr className="bg-muted/20">
                    <td colSpan={columns?.length} className="px-4 py-4">
                      <div className="grid grid-cols-3 gap-6">
                        <div>
                          <h4 className="text-sm font-semibold text-foreground mb-3">Contract Details</h4>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Contract Ref:</span>
                              <span className="text-foreground font-data">{shipment?.contractRef}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Incoterm:</span>
                              <span className="text-foreground">{shipment?.incoterm}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Port:</span>
                              <span className="text-foreground">{shipment?.port}</span>
                            </div>
                          </div>
                        </div>
                        <div>
                          <h4 className="text-sm font-semibold text-foreground mb-3">Pricing Details</h4>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Base Price:</span>
                              <span className="text-foreground font-data">${shipment?.basePrice}/MT</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Discount:</span>
                              <span className="text-foreground font-data">{shipment?.discount}%</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Platts Index:</span>
                              <span className="text-foreground">{shipment?.plattsIndex}</span>
                            </div>
                          </div>
                        </div>
                        <div>
                          <h4 className="text-sm font-semibold text-foreground mb-3">Documents</h4>
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 text-sm text-primary hover:underline cursor-pointer">
                              <Icon name="FileText" size={14} />
                              <span>Contract Agreement.pdf</span>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-primary hover:underline cursor-pointer">
                              <Icon name="FileText" size={14} />
                              <span>LC Document.pdf</span>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-primary hover:underline cursor-pointer">
                              <Icon name="FileText" size={14} />
                              <span>B/L Copy.pdf</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ShipmentDataGrid;